/* eslint-disable @typescript-eslint/no-unused-vars */
/* ─── Outlook-paneel: projectmail via submap of zoekopdracht ─────────────── */
import { useState, useEffect, useCallback } from "react";
import type { Email } from "./types";
import { I } from "./shared";

/* ─── Outlook Panel ──────────────────────────────────────────────────────── */
// Zoekstrategie:
// 1. Doorzoek top-level mappen en hun submappen op naam die begint met projectNumber
// 2. Als gevonden: toon mails uit die submap (met mapnaam als label)
// 3. Fallback: $search op projectNumber in alle mail
export function OutlookPanel({graphClient,projectNumber,color}:{graphClient:any;projectNumber:string;color:string}){
  const[emails,sEm]=useState<Email[]>([]);const[ld,sLd]=useState(false);const[sel,sSel]=useState<Email|null>(null);
  const[cs,sCs]=useState("");const[folderName,sFolderName]=useState<string|null>(null);
  const[err,sErr]=useState("");

  const findProjectFolder=useCallback(async(pn:string):Promise<{id:string;name:string}|null>=>{
    // Haal top-level mappen op (max 50)
    try{
      const topResp=await graphClient.api("/me/mailFolders").top(50).get();
      const topFolders=(topResp.value||[]) as any[];
      for(const folder of topFolders){
        // Controleer of de map zelf begint met het projectnummer
        if(folder.displayName?.startsWith(pn)){
          return{id:folder.id,name:folder.displayName};
        }
        // Heeft de map submappen? Doorzoek ze
        if(folder.childFolderCount>0){
          try{
            const childResp=await graphClient.api(`/me/mailFolders/${folder.id}/childFolders`).top(100).get();
            const children=(childResp.value||[]) as any[];
            const match=children.find((c:any)=>c.displayName?.startsWith(pn));
            if(match)return{id:match.id,name:match.displayName};
            // Nog een niveau dieper (bijv. OFFERTES > submap > projectmap)
            for(const child of children){
              if(child.childFolderCount>0){
                try{
                  const grandResp=await graphClient.api(`/me/mailFolders/${child.id}/childFolders`).top(100).get();
                  const grandchildren=(grandResp.value||[]) as any[];
                  const gMatch=grandchildren.find((g:any)=>g.displayName?.startsWith(pn));
                  if(gMatch)return{id:gMatch.id,name:gMatch.displayName};
                }catch(e){}
              }
            }
          }catch(e){}
        }
      }
    }catch(e){console.warn("Mappen doorzoeken mislukt:",e)}
    return null;
  },[graphClient]);

  const fetchEmails=useCallback(async(pn:string,customSearch?:string)=>{
    sLd(true);sErr("");sSel(null);
    try{
      if(customSearch){
        // Handmatig zoeken: gebruik $search in alle mail
        const u=`/me/messages?$top=40&$orderby=receivedDateTime desc&$select=id,subject,from,receivedDateTime,isRead,bodyPreview,webLink,hasAttachments&$search="${customSearch.trim()}"`;
        const r=await graphClient.api(u).get();
        sEm(r.value||[]);sFolderName(null);
      }else{
        // Automatisch: zoek submap op projectnummer
        const folder=await findProjectFolder(pn);
        if(folder){
          // Mails uit de specifieke map ophalen (geen $search, gewoon alles gesorteerd)
          const r=await graphClient.api(`/me/mailFolders/${folder.id}/messages`)
            .top(50).orderby("receivedDateTime desc")
            .select("id,subject,from,receivedDateTime,isRead,bodyPreview,webLink,hasAttachments,internetMessageId")
            .get();
          sEm(r.value||[]);sFolderName(folder.name);
        }else{
          // Fallback: $search op projectnummer
          const u=`/me/messages?$top=40&$orderby=receivedDateTime desc&$select=id,subject,from,receivedDateTime,isRead,bodyPreview,webLink,hasAttachments,internetMessageId&$search="${pn}"`;
          const r=await graphClient.api(u).get();
          sEm(r.value||[]);sFolderName(null);
        }
      }
    }catch(e:any){sErr("Fout: "+(e.message||""))}
    sLd(false);
  },[graphClient,findProjectFolder]);

  useEffect(()=>{sCs("");fetchEmails(projectNumber)},[projectNumber,fetchEmails]);

  const handleSearch=()=>{
    if(cs.trim())fetchEmails(projectNumber,cs);
    else fetchEmails(projectNumber);
  };

  const sourceLabel=folderName?`📁 ${folderName}`:`🔍 ${projectNumber}`;

  return <div style={{display:"flex",flex:1,minHeight:0}}>
    <div style={{width:sel?220:"100%",borderRight:sel?"1px solid #E8E8E8":"none",display:"flex",flexDirection:"column",flexShrink:0,minWidth:sel?220:0}}>
      <div style={{padding:"6px 8px",borderBottom:"1px solid #E8E8E8",display:"flex",gap:4,alignItems:"center"}}>
        <div style={{flex:1,display:"flex",alignItems:"center",gap:4,background:"#F5F5F5",borderRadius:4,padding:"4px 8px"}}><I.Search/><input value={cs} onChange={e=>sCs(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")handleSearch()}} placeholder="Zoeken..." style={{flex:1,border:"none",outline:"none",fontSize:11,background:"transparent",color:"#1A1A1A",fontFamily:"Segoe UI"}}/></div>
        {cs&&<button onClick={()=>{sCs("");fetchEmails(projectNumber)}} style={{fontSize:10,color:"#9AA0A6",background:"none",border:"none",cursor:"pointer"}}>Reset</button>}
      </div>
      <div style={{padding:"4px 8px",fontSize:10,color:"#9AA0A6",borderBottom:"1px solid #F5F5F5",display:"flex",alignItems:"center",gap:4}}>
        <span style={{color:folderName?"#107C10":color,fontWeight:600}}>{sourceLabel}</span>
        {folderName&&<span style={{marginLeft:"auto",fontSize:9,color:"#9AA0A6"}}>map</span>}
      </div>
      {ld&&<div style={{padding:12,color:"#9AA0A6",fontSize:11}}>Laden...</div>}
      <div style={{flex:1,overflowY:"auto"}}>
        {emails.map(email=><div key={email.id} onClick={()=>sSel(email)} style={{padding:"7px 9px",cursor:"pointer",borderBottom:"1px solid #F5F5F5",background:sel?.id===email.id?color+"10":"transparent"}} onMouseEnter={e=>{if(sel?.id!==email.id)e.currentTarget.style.background="#F9F9F9"}} onMouseLeave={e=>{if(sel?.id!==email.id)e.currentTarget.style.background="transparent"}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}>{!email.isRead&&<span style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0}}/>}<span style={{fontSize:11,fontWeight:email.isRead?400:700,color:"#1A1A1A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{email.from?.emailAddress?.name||"Onbekend"}</span><span style={{fontSize:9,color:"#9AA0A6",flexShrink:0}}>{new Date(email.receivedDateTime).toLocaleDateString("nl-NL",{day:"numeric",month:"short"})}</span></div>
          <div style={{fontSize:11,fontWeight:email.isRead?400:600,color:"#333",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{email.subject||"(geen)"}</div>
          {!sel&&<div style={{fontSize:10,color:"#9AA0A6",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{email.bodyPreview}</div>}
        </div>)}
        {!ld&&emails.length===0&&<div style={{padding:20,textAlign:"center",color:"#9AA0A6",fontSize:12}}>Geen mail gevonden{folderName?` in map "${folderName}"`:` voor "${projectNumber}"`}.</div>}
      </div>
    </div>
    {sel&&<div style={{flex:1,display:"flex",flexDirection:"column",padding:"10px 14px",overflowY:"auto",minWidth:0}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <button onClick={()=>sSel(null)} style={{fontSize:10,color:"#5F6368",background:"none",border:"1px solid #E0E0E0",borderRadius:4,padding:"3px 8px",cursor:"pointer",fontFamily:"Segoe UI"}}>{"\u2190"}</button>
        <div style={{display:"flex",gap:4}}>
          {/* Outlook desktop: zonder target="_blank" geeft Windows de link door aan de standaard mail-app (nieuwe Outlook) */}
          <a href={sel.webLink}
            title="Openen in Outlook"
            style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,color:"#0078D4",textDecoration:"none",border:"1px solid #0078D440",borderRadius:4,padding:"3px 8px",fontWeight:600}}>
            <I.Mail/> Outlook
          </a>
        </div>
      </div>
      <h3 style={{fontSize:14,fontWeight:700,margin:"0 0 4px",color:"#1A1A1A"}}>{sel.subject}</h3>
      <div style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"#9AA0A6",marginBottom:12}}><span style={{fontWeight:600,color:"#5F6368"}}>{sel.from?.emailAddress?.name}</span><span style={{color:"#9AA0A6"}}>{sel.from?.emailAddress?.address}</span><span>{new Date(sel.receivedDateTime).toLocaleDateString("nl-NL",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>{sel.hasAttachments&&<span><I.Attach/> Bijlage</span>}</div>
      <div style={{fontSize:12,lineHeight:1.7,color:"#333",whiteSpace:"pre-wrap"}}>{sel.bodyPreview}</div>
    </div>}
  </div>
}
